import React, { useEffect, useState } from 'react';
import { 
  Building2, 
  Users, 
  DollarSign, 
  TrendingUp,
  Timer,
  UserX
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  ResponsiveContainer 
} from 'recharts';
import { useTitle } from '../../../context/TitleContext';
import { MetricCard } from '../MetricCard';
import { getDashboardMetrics, getAnalytics } from '../../../lib/adminQueries';
import { DashboardMetrics, AnalyticsDataPoint } from '../../../models/admin';

export const AdminAnalytics: React.FC = () => {
  const { setTitle } = useTitle();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [signupsData, setSignupsData] = useState<AnalyticsDataPoint[]>([]);
  const [conversionsData, setConversionsData] = useState<AnalyticsDataPoint[]>([]);
  const [revenueData, setRevenueData] = useState<AnalyticsDataPoint[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setTitle('Admin Analytics');
    document.title = 'Admin Analytics';
  }, [setTitle]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [metricsData, analyticsData] = await Promise.all([
          getDashboardMetrics(),
          getAnalytics({
            start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
            end: new Date(),
          }),
        ]);

        setMetrics(metricsData);
        setSignupsData(analyticsData.signups);
        setConversionsData(analyticsData.conversions);
        setRevenueData(analyticsData.revenue);
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="ml-3 text-gray-600 dark:text-gray-400">Loading analytics...</p>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-600 dark:text-gray-400">Failed to load analytics data.</p>
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admin Analytics</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Overview of key metrics and performance indicators
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <MetricCard
          title="Total Organizations"
          value={metrics.totalOrganizations}
          icon={Building2}
          colorClass="bg-blue-500"
          subtitle={`${metrics.activeTrials} on trial`}
        />
        <MetricCard
          title="Active Trials"
          value={metrics.activeTrials}
          icon={Timer}
          colorClass="bg-yellow-500"
          trend={{
            value: 12.5,
            isPositive: true,
          }}
        />
        <MetricCard
          title="Active Subscriptions"
          value={metrics.activeSubscriptions}
          icon={Users}
          colorClass="bg-green-500"
          trend={{
            value: 8.3,
            isPositive: true,
          }}
        />
        <MetricCard
          title="Monthly Recurring Revenue"
          value={formatCurrency(metrics.monthlyRecurringRevenue)}
          icon={DollarSign}
          colorClass="bg-emerald-500"
          trend={{
            value: 15.2,
            isPositive: true,
          }}
        />
        <MetricCard
          title="Trial Conversion Rate"
          value={`${metrics.trialConversionRate.toFixed(1)}%`}
          icon={TrendingUp}
          colorClass="bg-purple-500"
          trend={{
            value: 3.2,
            isPositive: true,
          }}
        />
        <MetricCard
          title="Churned This Month"
          value={metrics.churnedThisMonth}
          icon={UserX}
          colorClass="bg-red-500"
          trend={{
            value: 2.1,
            isPositive: false,
          }}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sign-ups Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Sign-ups (Last 30 Days)
          </h2>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={signupsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis 
                dataKey="date" 
                stroke="#9CA3AF"
                fontSize={12}
              />
              <YAxis stroke="#9CA3AF" fontSize={12} />
              <Tooltip 
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#F9FAFB',
                }}
              />
              <Area 
                type="monotone" 
                dataKey="value" 
                stroke="#3B82F6" 
                fill="#3B82F6" 
                fillOpacity={0.6}
                name="Sign-ups"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Conversions Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Trial Conversions (Last 30 Days)
          </h2>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={conversionsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis 
                dataKey="date" 
                stroke="#9CA3AF"
                fontSize={12}
              />
              <YAxis stroke="#9CA3AF" fontSize={12} />
              <Tooltip 
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#F9FAFB',
                }}
              />
              <Bar dataKey="value" fill="#10B981" name="Conversions" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 lg:col-span-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Revenue Growth (Last 30 Days)
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis 
                dataKey="date" 
                stroke="#9CA3AF"
                fontSize={12}
              />
              <YAxis 
                stroke="#9CA3AF" 
                fontSize={12}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#F9FAFB',
                }}
                formatter={(value) => [`$${value}`, 'Revenue']}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke="#8B5CF6" 
                strokeWidth={2}
                dot={{ fill: '#8B5CF6' }}
                name="Revenue"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Additional Stats */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Quick Stats
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border-l-4 border-blue-500 pl-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">Average Trial Duration</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">14 days</p>
          </div>
          <div className="border-l-4 border-green-500 pl-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">Average Customer Lifetime</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">8.4 months</p>
          </div>
          <div className="border-l-4 border-purple-500 pl-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">Average MRR per Customer</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(metrics.monthlyRecurringRevenue / Math.max(1, metrics.activeSubscriptions))}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
